import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText } from '@mui/material';
import { AxiosError } from 'axios';
import { FormSelect, FormTextField } from 'components/forms';
import { useSnackbar } from 'notistack';
import { tierOverrideContext } from 'pages/tierSetUp/tierSetUpResolver';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSendTierChangeApprovedEmail } from 'shared/hooks/sendTierChangeApprovedEmail';
import { useSendTierChangeRejectedEmail } from 'shared/hooks/sendTierChangeRejectedEmail';
import { useHttp } from 'shared/providers/provide-http';
import { DynamicEmailData } from 'shared/services/email-address-service';
import { useAuthState } from 'shared/stores';
import { ApprovalTierChangeEmailData, CustomerTierOverrideData } from 'types/backend';

interface EditTierOverrideStatusFormProps {
    open: boolean;
    row?: CustomerTierOverrideData;
    onClose: () => void;
}

const options = [
    { value: 'Approved', displayText: 'Approved' },
    { value: 'Rejected', displayText: 'Rejected' },
];

export function EditTierOverrideStatusForm({ row, open, onClose }: EditTierOverrideStatusFormProps) {
    const {
        control,
        formState: { errors },
        handleSubmit,
    } = useForm<CustomerTierOverrideData>({
        defaultValues: {
            id: '',
            approvalStatus: '',
            comments: '',
        },
    });
    const http = useHttp();
    const { enqueueSnackbar } = useSnackbar();
    const [error, setError] = useState<string>();
    const tierOverrideApprovalContext = useContext(tierOverrideContext);
    const url = window.location.origin;

    const authState = useAuthState();
    const currentUser = authState.user.get()?.displayName;

    const dynamicEmailData: Omit<DynamicEmailData, 'currentUserName' | 'currentUserEmailAddress'> = {
        territorySalesLeaderName: row?.territorySalesLeader || '',
        territorySalesLeaderEmail: row?.territorySalesLeaderEmail || '',
        territorySalesRepName: row?.territorySalesRep || '',
        territorySalesRepEmail: row?.territorySalesRepEmail || '',
    };

    const sendApprovalEmail = useSendTierChangeApprovedEmail(`${url}/approvals/customerTierOverride`, dynamicEmailData);
    const sendRejectedEmail = useSendTierChangeRejectedEmail(`${url}/approvals/customerTierOverride`, dynamicEmailData);

    const confirmClickHandler = handleSubmit(async (form: CustomerTierOverrideData) => {
        if (row) {
            const request = {
                id: row.id,
                approvalStatus: form.approvalStatus,
                comment: form.comments,
            };
            try {
                await http.put('/api/customerTierOverride', request);
                enqueueSnackbar('Status change successful', { variant: 'success' });

                const tierChangeEmailData: ApprovalTierChangeEmailData = {
                    customerName: row?.branchname ?? 'There was a problem getting customer name',
                    approver: 'Clay Tiedeman',
                    createdBy: currentUser ?? 'There was a problem getting the user',
                    customerAccount: row?.accountNumber ?? 'There was a problem getting the account',
                    territorySalesLeader: row?.territorySalesLeader ?? '',
                    territorySalesRep: row?.territorySalesRep ?? '',
                    revisedTier: row.tierOverride,
                    currentTier: row.currentTier,
                    estAnnualVolume: row.ttmpounds.toString(),
                    tierRevisionReason: row.revisionReason,
                    requestorComments: row.comments,
                    requestorEmail: row.requestorEmail,
                };

                if (form.approvalStatus === 'Approved') sendApprovalEmail.mutateAsync(tierChangeEmailData);
                if (form.approvalStatus === 'Rejected') sendRejectedEmail.mutateAsync(tierChangeEmailData);

                tierOverrideApprovalContext.refreshData();
                onClose();
            } catch (error) {
                const axiosErrorResponse = (error as AxiosError).response;
                const errorText =
                    axiosErrorResponse?.status === 400 && axiosErrorResponse?.data
                        ? (axiosErrorResponse?.data as string)
                        : `${axiosErrorResponse?.statusText} - ${axiosErrorResponse?.status}`;

                setError(errorText);
                enqueueSnackbar('Status change failed', { variant: 'error' });
            }
        }
    });
    return (
        <Dialog open={open}>
            <DialogTitle>{`Tier Override for ${row?.branchname}`}</DialogTitle>
            <DialogContent>
                <Box sx={{ paddingTop: '20px' }}>
                    <FormSelect
                        name={'approvalStatus'}
                        label='Status'
                        control={control}
                        rules={{ required: 'Status is required' }}
                        options={options}
                        fieldError={errors?.approvalStatus}
                    />
                    <FormHelperText error sx={{ paddingLeft: '15px' }}>
                        {errors?.approvalStatus?.message}
                    </FormHelperText>
                </Box>
                <Box sx={{ paddingTop: '20px' }}>
                    <FormTextField name={'comments'} label='Comments' control={control} fieldError={errors?.comments} />
                </Box>
                {error && <Alert severity='error'>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={onClose}>
                    Exit
                </Button>
                <Button onClick={confirmClickHandler} autoFocus>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}
